// Home's Needs you strip (home.js): everything waiting on the person, in orange, at the top; a tap opens it. Each need
// is its own row, full width — its title, what it waits on, its buttons — never pills side by side (Josh 10-16, phone). A Claude
// conversation waiting on a permission shows the ask itself there too — what it wants to run and Deny / Allow once
// (claudeask/, Josh 10-10: the phone said it needed approval and offered no way to give it).
//
// A card with `expires` (an Approvals row) shows its time left: the border drains, "N min left" (countdown.js; home.js
// ticks it each second; `home.now()` stands in for the clock in tests).
//
// Seams: `needsStrip(home, m, items)` → its HTML; `needsTap(home, macKey, rest)` — a `cask:<mac>|<item key>|<choice>`.
import { icon } from '../../core/icons.js'
import { askOf } from '../../claudeask/ask.js'
import { askState } from '../../claudeask/answer.js'
import { stripRow } from '../../claudeask/view.js'
import { countdownCard } from './countdown.js'

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

/// A Home item's ask: a Claude Code extension row (its id is the glance's line) waiting on a permission.
export function askOfItem(it) {
  const id = String(it?.row?.id || '')
  return it?.ext && /^(conversation|session):./.test(id) ? askOf(it.row.asks, id) : null
}

export function needsStrip(home, m, items) {
  const waiting = items.filter((it) => it.status === 'waiting')
  if (!waiting.length) return ''
  return `<div class="ht-needs"><div class="ht-needshead"><img src="${icon('hand.raised.fill', 'var(--c-orange)')}">Needs you · ${waiting.length}</div>` +
    waiting.slice(0, 6).map((it) => {
      const ask = askOfItem(it), open = home.tapOf(m, it)
      if (ask) return stripRow(ask, askState(m.key, ask), { act: open, title: it.title }, (choice) => `cask:${m.key}|${it.key}|${choice}`)
      // a card (an approval, a pairing — this Mac's or a linked Mac's): the card itself, its buttons answering in one
      // tap (Josh 10-14, phone: "can't it be a modal from the 'needs you' top message?")
      const acts = it.row?.actions || []
      if (acts.length && !acts.some((a) => a.primary)) {
        // its time left (Josh 10-14): the border drains, "N min left" in its line, "Timed out" and no buttons at zero (countdown.js)
        const timer = countdownCard(it.row, home.now ? home.now() : Date.now())
        return `<div class="ht-needcard${timer.cls}"${timer.attrs}><div class="ht-needtitle">${esc(it.title)}</div>` +
          (it.row.code ? codeBlock(home, m, it) : '') + (it.row.folder ? `<div class="ht-needline">${esc(it.row.folder)}</div>` : '') +
          // who a remote agent says asked ("from session … on Mini" — Josh 10-16; the agent's claim, words only)
          (it.row.from ? `<div class="ht-needline ht-needfrom">${esc(it.row.from)}</div>` : '') +
          (timer.c ? `<div class="ht-needline ht-needleft">${esc(timer.c.words)}</div>` : '') +
          // every button the card has (an agent's command: Deny · Allow once · Allow this command · Allow until… · Allow this
          // session · Allow always · Auto for this agent on ‹Mac› — Josh 10-14, 10-17), wrapping under the words
          (timer.c?.done ? '' : home.answered?.has(m.key + '|' + it.key)
            ? `<div class="ht-needline ht-sent">Sent · waiting for ${esc(it.row.badge && /^on /.test(it.row.badge) ? it.row.badge.slice(3) : m.name)}</div>`
            : cardButtons(home, m, it, acts)) + `</div>`
      }
      // one ask, one row (Josh 10-16: three asks squeezed onto one line on the phone): its title — the tap that opens
      // it — and under it what it waits on (the row's badge, else where it is)
      const detail = it.badge && it.badge !== it.kindLabel ? it.badge : it.where
      return `<div class="ht-needrow" role="group" aria-label="${esc(it.title)}"><button class="ht-need" data-act="${esc(open)}">${esc(it.title)}</button>` +
        (detail ? `<div class="ht-needline">${esc(detail)}</div>` : '') + `</div>`
    }).join('') + `</div>`
}

/// A card's buttons. An agent's command (Josh 10-17: "Allow until… with a time he picks"): the actions marked `until`
/// (its quick choices — Allow 30 min · 1 hour · 4 hours · until 8:00 AM) are one "Allow until…" button opening a chooser
/// with them and a date-and-time picker; its Auto gets an "Auto until…" beside it, the same choices as `auto+…` / `auto@…`.
/// The rest are buttons, each answering in one tap. `home.untilOpen`: "<mac>|<item>" → the chooser open ("minutes" | "auto").
function cardButtons(home, m, it, acts) {
  const ik = m.key + '|' + it.key, open = home.untilOpen?.get(ik)
  const btn = (a, i) => `<button class="ht-cardact${/deny|cancel|no\b/i.test(a.title || a.id) ? ' quiet' : ''}" data-act="xcard:${esc(m.key)}|${esc(it.key)}|${i}">${esc(a.title || a.id)}</button>`
  const opener = (kind, words) => `<button class="ht-cardact${open === kind ? ' on' : ''}" data-act="xuntil:${esc(m.key)}|${esc(it.key)}|${kind}">${esc(words)}</button>`
  let out = '', untilShown = false
  acts.forEach((a, i) => {
    if (a.until) { if (!untilShown) { untilShown = true; out += opener('minutes', 'Allow until…') } return }
    out += btn(a, i)
    if (a.id === 'auto' && acts.some((x) => x.until)) out += opener('auto', 'Auto until…')
  })
  return `<div class="ht-cardacts">${out}</div>` + (open ? untilChooser(m, it, acts, open, home.now ? home.now() : Date.now(), home.untilValue) : '')
}

/// The chooser: the quick choices, then a time of one's own (a minute to 31 days away — the Mac's AllowUntil). `picked`:
/// the picker's value so far (home.untilValue), kept across renders.
function untilChooser(m, it, acts, kind, now, picked) {
  const quick = acts.filter((a) => a.until).map((a) => {
    const prefix = a.untilPrefix || '', bare = a.id.slice(prefix.length)
    return kind === 'auto' ? { id: prefix + bare.replace(/^minutes/, 'auto'), title: String(a.title || '').replace(/^Allow/, 'Auto') } : { id: a.id, title: a.title }
  })
  const pad = (n) => String(n).padStart(2, '0'), at = (t) => { const d = new Date(t); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }
  const go = (id) => `xuntilgo:${esc(m.key)}|${esc(it.key)}|${esc(id)}`
  return `<div class="ht-until" data-kind="${kind}">` +
    quick.map((q) => `<button class="ht-cardact" data-act="${go(q.id)}">${esc(q.title)}</button>`).join('') +
    `<label class="ht-untilpick">${kind === 'auto' ? 'Auto until' : 'Allow until'} <input type="datetime-local" class="ht-untiltime" value="${esc(picked || at(now + 3600e3))}" min="${at(now + 60e3)}" max="${at(now + 31 * 86400e3)}"></label>` +
    `<button class="ht-cardact" data-act="${go('pick')}">${kind === 'auto' ? 'Auto until this time' : 'Allow until this time'}</button></div>`
}

/// The chooser opened or closed (`xuntil:<mac>|<item>|<kind>`).
export function untilToggle(home, macKey, rest) {
  const at = rest.lastIndexOf('|'), ik = macKey + '|' + rest.slice(0, at), kind = rest.slice(at + 1)
  home.untilOpen = home.untilOpen || new Map()
  if (home.untilOpen.get(ik) === kind) home.untilOpen.delete(ik); else home.untilOpen.set(ik, kind)
  home.untilValue = null
  home.render(home.state)
}

/// A choice in it (`xuntilgo:<mac>|<item>|<action id | pick>`): the action sent as any of the card's, a time picked as
/// `‹prefix›minutes@‹unix seconds›` (or `auto@…`). `value`: the picker's, read off the page. False when nothing was sent.
export function untilSend(home, macKey, rest, value) {
  const at = rest.lastIndexOf('|'), itemKey = rest.slice(0, at), choice = rest.slice(at + 1)
  const m = home.macOf(macKey), it = m && home.items(m).find((x) => x.key === itemKey), acts = it?.row?.actions || []
  const first = acts.find((a) => a.until)
  if (!it || !first) return false
  const kind = home.untilOpen?.get(macKey + '|' + itemKey) || 'minutes'
  let id = choice
  if (choice === 'pick') {
    const t = Date.parse(String(value || '')), now = home.now ? home.now() : Date.now()
    if (!Number.isFinite(t) || t < now + 60e3 || t > now + 31 * 86400e3) return false
    id = (first.untilPrefix || '') + kind + '@' + Math.floor(t / 1000)
  }
  home.untilOpen?.delete(macKey + '|' + itemKey)
  home.answered?.set(macKey + '|' + itemKey, Date.now())
  home.a.extAction?.(macKey, it.ext, { action: id, args: first.args || {}, target: 'home' })
  home.render(home.state)
  return true
}

/// The card's command or script, as lines (Josh 10-15: "it should look like the allow / always allow prompts"): a script
/// or a command of several lines shows each line — the first 12, then "N more lines" opens the rest (scrolling inside
/// the card, "Fewer lines" folds it); one long line is clamped with Show all / Less (Josh 10-14: "it goes all the way off
/// my screen", then "text overflow there doesn't let me scroll down"). Open ones are kept per row (home.codeOpen).
function codeBlock(home, m, it) {
  const key = m.key + '|' + it.key, fold = codeFold(it.row.code, home.codeOpen?.has(key))
  return `<code class="ht-code${fold.cls ? ' ' + fold.cls : ''}">${esc(fold.text)}</code>` +
    (fold.toggle ? `<button class="ht-codemore" data-act="codeopen:${esc(key)}">${esc(fold.toggle)}</button>` : '')
}

export const CODE_LINES = 12

/// What the card's code block shows: {text, cls, toggle} — `toggle` the button's words, null for none.
export function codeFold(code, open) {
  const text = String(code ?? ''), lines = text.replace(/\n$/, '').split('\n')
  if (lines.length > CODE_LINES) {
    if (open) return { text: lines.join('\n'), cls: 'lines open', toggle: 'Fewer lines' }
    const more = lines.length - CODE_LINES
    return { text: lines.slice(0, CODE_LINES).join('\n'), cls: 'lines', toggle: `${more} more line${more === 1 ? '' : 's'}` }
  }
  if (lines.length > 1) return { text: lines.join('\n'), cls: 'lines', toggle: null }
  return { text, cls: open ? 'open' : '', toggle: text.length > 160 ? (open ? 'Less' : 'Show all') : null }
}

/// A strip's Deny / Allow once: the item's ask, answered on its Mac (app.js `claudeAsk`).
export function needsTap(home, macKey, rest) {
  const at = rest.lastIndexOf('|'), ik = rest.slice(0, at), choice = rest.slice(at + 1)
  const m = home.macOf(macKey), it = m && home.items(m).find((x) => x.key === ik), ask = askOfItem(it)
  if (ask && choice) home.a.claudeAsk?.(macKey, ask, choice)
}
